import { DateTime } from "luxon";
import moment from "moment";

import { DateHelper } from "@/Common/Helpers/DateHelper";
import { AttachedObjectResponse } from "@/Common/Models/FileSelection/Responses/AttachedObject";
import { Guid } from "@/Common/Models/Guid";
import { Comment } from "@/Common/Models/Patient/Comment";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { ConsultationRequestPriorityOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { DirectoryEntityType } from "@/ContactDirectory/Api/medfar-contactdirectory/dist";
import { DirectorySelectableEntity } from "@/ContactDirectory/Models/DirectorySelectableEntity";
import { IDirectoryEntity } from "@/ContactDirectory/Models/IDirectoryEntity";
import { IMedicalRequest } from "./IMedicalRequest";
import { ConsultationRequestsInboxItem } from "./MyNotesInboxItem";

class ConsultationRequest implements IMedicalRequest {
    private _comments: Comment[];

    public id: Guid;
    public requestingStaffContactFacetId: Guid;
    public isSupervisorRequired: boolean;
    public priorityDropDownId: string;
    public reason: string;
    public isFaxed: boolean;
    public faxDate: Date;
    public isPrinted: boolean;
    public requestDate: Date;
    public encounterId: Guid;
    public isInvalid: boolean;
    public statusLookupId: string;
    public firstPrintedByEmployeeId: Guid;
    public firstPrintedDate: Date;
    public dueDate: Date;
    public dueDateText: string;
    public statusChangeDate: Date;
    public isVisibleInCPP: boolean;
    public createdByContactId: Guid;
    public sharedItemId: string;
    public sharedItem: SharedItem;
    public sharedItemMessages: SharedItemMessage[];
    public signedPrintAuthorizations: SignedPrintAuthorization[];
    public attachedDocuments: AttachedObjectResponse[];
    public patientContactId: string;
    public isAccessProhibited: boolean;

    /**
     * Body is equivalent to description, but specific to this class.
     * */
    public body: string;
    public speciality: string;
    public referralConsultationDate?: DateTime;
    public referralConsultationDateText?: string;
    public recipient: string | null;
    public recipientContactDirectoryEntityId: string | null;
    public recipientContactDirectoryEntityType: DirectoryEntityType | null;
    public recipientDirectorySelectedEntity: DirectorySelectableEntity<IDirectoryEntity> | null;
    public recipientSearchText: string;

    public lastUpdatedByContactId: Guid;
    public lastUpdateDate: Date;

    constructor(fields?: Partial<ConsultationRequest>) {
        Object.assign(this, fields);
    }

    /**
    * Provides a common field for a medical request's body text between classes implementing IMedicalRequest
    * */
    public get description(): string {
        return this.body;
    }

    public set description(_description: string) {
        this.body = _description;
    }

    public get comments(): Comment[] {
        return this._comments || [];
    }

    public set comments(comments: Comment[]) {
        this._comments = comments || [];
    }

    public getPriority(): ConsultationRequestPriorityOption {
        return ConsultationRequestPriorityOption.fromDropDownId(this.priorityDropDownId);
    }

    public isUrgent(): boolean {
        return ConsultationRequestPriorityOption.Urgent.model.Id === this.priorityDropDownId;
    }

    public getStatusLocalizedValue(): string {
        return this.statusLookupId ? cacheStore.GetLocalizedValueByLookupId(this.statusLookupId) : "";
    }

    public static fromComposite(item: ConsultationRequestsInboxItem): ConsultationRequest {
        if (!item)
            return null;

        return new ConsultationRequest({
            id: item.ConsultationRequestId,
            requestingStaffContactFacetId: item.RequestingStaffContactFacetId,
            isSupervisorRequired: item.IsSupervisorRequired,
            priorityDropDownId: item.PriorityDropdownId || ConsultationRequestPriorityOption.Normal.model.Id,
            reason: item.Reason,
            isFaxed: item.IsFaxed,
            isPrinted: item.IsPrinted,
            requestDate: item.ConsultationRequestDate,
            encounterId: item.EncounterId,
            description: item.Description,
            speciality: item.Speciality,
            recipientSearchText: item.Recipient,
            lastUpdatedByContactId: item.LastUpdateByContactId,
            lastUpdateDate: item.LastUpdateDate,
            isInvalid: item.IsInvalid,
            firstPrintedByEmployeeId: item.FirstPrintedByEmployeeId,
            firstPrintedDate: item.FirstPrintedDate,
            statusLookupId: item.StatusLookupId,
            comments: item.Comments,
            dueDate: item.DueDate,
            dueDateText: item.DueDate ? moment(item.DueDate).format("YYYY-MM-DD") : "",
            isVisibleInCPP: item.IsVisibleInCPP,
            createdByContactId: item.CreatedByContactId,
            sharedItemId: item.SharedItemId,
            sharedItem: item.SharedItem,
            sharedItemMessages: item.SharedItemMessages,
            attachedDocuments: item.AttachedDocuments,
            patientContactId: item.PatientContactId,
            signedPrintAuthorizations: item.SignedPrintAuthorizations,
            referralConsultationDate: LuxonDateTimeParser.parse(item.ReferralConsultationDate),
            referralConsultationDateText: DateHelper.formatIsoDateOnly(item.ReferralConsultationDate) ?? "",
            recipient: item.Recipient,
            recipientContactDirectoryEntityId: item.RecipientContactDirectoryEntityId,
            recipientContactDirectoryEntityType: item.RecipientContactDirectoryEntityType,
            recipientDirectorySelectedEntity: item.RecipientDirectorySelectedEntity
        });
    }

    public toJSON(): object {
        return {
            ...this as object,
            recipient: this.recipient
        };
    }
}

export { ConsultationRequest };
